import moment from 'moment';
import type { DisableUnAvailableDates } from './types';
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';

const toUtcDay = (value) => moment.utc(value).startOf('day');

const isInRangeInclusiveUtc = (day, start, end) => {
  const d = toUtcDay(day);
  const s = toUtcDay(start);
  if (!d.isValid() || !s.isValid()) return false;

  if (end == null) {
    return d.isSameOrAfter(s, 'day');
  }

  const e = toUtcDay(end);
  if (!e.isValid()) return false;
  return d.isSameOrAfter(s, 'day') && d.isSameOrBefore(e, 'day');
};

export const getMaxSelectableEndDate = (
  unavailableDates: (string | null)[] = [],
  selectedStartDate: string,
) => {
  if (!selectedStartDate) return null;

  const startDay = toUtcDay(selectedStartDate);
  if (!startDay.isValid()) return null;

  // Find the next unavailable *start* after the selected start date.
  const nextStarts = (unavailableDates ?? [])
    .map((d) => d?.[0])
    .filter(Boolean)
    .map((d) => toUtcDay(d))
    .filter((m) => m.isValid() && m.isAfter(startDay, 'day'))
    .sort((a, b) => a.valueOf() - b.valueOf());

  if (!nextStarts.length) return null;

  return formatDateDB(nextStarts[0].clone().subtract(1, 'day'));
};

export const getNextClosestAvailableDate = (
  datesArray: (string | null)[] = [],
  referenceDate: string,
  currentEditingIndex: number,
) => {
  // Convert referenceDate to a formatted string
  const referenceDateString = formatDateDB(referenceDate);
  let closestDateString = null;

  for (let i = 0; i < datesArray.length; i++) {
    for (let j = 0; j < datesArray[i].length; j++) {
      let date = datesArray[i][j];
      if (date !== null) {
        // Convert date to a formatted string
        const dateStr = formatDateDB(date);
        if (
          dateStr > referenceDateString &&
          (closestDateString === null || dateStr < closestDateString) &&
          i !== currentEditingIndex
        ) {
          closestDateString = dateStr;
        }
      }
    }
  }

  return closestDateString ?? null;
};

const processUnavailableDates = (
  unAvailableDates,
  currentDay,
  editMode,
  isEndDateField,
  currentEditingIndex,
  currentEditingMaxEndDate,
): boolean => {
  return unAvailableDates.some((date, index) => {
    const [startDateString, endDateString] = date;
    let isDisabled = false;

    // The caller is responsible for excluding the "currently editing" interval from `unAvailableDates`.
    // Here we simply disable any day that is within an unavailable interval (inclusive).
    isDisabled = isInRangeInclusiveUtc(
      currentDay,
      startDateString,
      endDateString,
    );

    // Check for endDateField and whether currentDay is after the currentEditingMaxEndDate
    if (currentEditingMaxEndDate && isEndDateField && !isDisabled) {
      isDisabled = toUtcDay(currentDay).isAfter(
        toUtcDay(currentEditingMaxEndDate),
        'day',
      );
    }

    return isDisabled;
  });
};

export const disableUnAvailableDates = ({
  unAvailableDates = [],
  actualOrAnticipatedMoveOutDate,
  editMode,
  isEndDateField,
  currentEditingIndex,
  currentEditingMaxEndDate,
  day,
}: DisableUnAvailableDates): boolean => {
  // Check for unavailable dates and process them
  let disableDay = unAvailableDates.length
    ? processUnavailableDates(
        unAvailableDates,
        day,
        editMode,
        isEndDateField,
        currentEditingIndex,
        currentEditingMaxEndDate,
      )
    : false;

  // Check if currentDay is after the actualOrAnticipatedMoveOutDate
  if (
    !disableDay &&
    actualOrAnticipatedMoveOutDate &&
    toUtcDay(day).isAfter(toUtcDay(actualOrAnticipatedMoveOutDate), 'day')
  ) {
    disableDay = true;
  }

  return disableDay;
};

export const calculateFinalMaxDate = ({
  unAvailableDates,
  selectedStartDate,
  editMode,
  currentEditingMaxEndDate,
}) => {
  let maxDate;

  const hasNullEndDate = unAvailableDates.some((date) => date[1] === null);

  // Convert selectedStartDate and currentEditingMaxEndDate to formatted strings
  const selectedStartDateString = formatDateDB(selectedStartDate);
  const currentEditingMaxEndDateString = currentEditingMaxEndDate
    ? formatDateDB(currentEditingMaxEndDate)
    : null;

  const maxDateCandidates = unAvailableDates
    .map((date) => formatDateDB(date[0]))
    .filter((startDateString) => startDateString > selectedStartDateString)
    .sort();

  if (maxDateCandidates.length > 0) {
    maxDate = maxDateCandidates[maxDateCandidates.length - 1];
  }

  const shouldSetMaxDate = editMode && hasNullEndDate;

  const finalMaxDate =
    currentEditingMaxEndDateString || (shouldSetMaxDate && maxDate)
      ? maxDate
      : undefined;

  return finalMaxDate;
};
